/**
 * Player avatars: the small portrait each player picks from a grid before a
 * game starts, shown beside their name in player lists and overlays.
 *
 * One system for every game type. A game switches it on in
 * {@link GAME_USES_AVATARS}. Joining gives a player a random free avatar; they
 * may change it until the first round starts. An avatar is unique per team:
 * players on different teams may hold the same one, but teammates may not, and
 * a player on no team yet may not share with anyone (they could join either
 * team). Players who left free theirs; once every avatar is taken, newcomers
 * share the least-used ones.
 *
 * The art is `shared-assets/served/common/avatars/<id>.svg`. Each avatar has its
 * own base colours, and team games recolour it in the team's tones.
 */
import { GameType } from '../constants/gameTypes';
import { HNST_TEAMS, getHnstTeamOf } from './hnst';
/** Every avatar, in grid order. */
export const PLAYER_AVATAR_IDS = [
    'human-a', 'human-b', 'twilek', 'zabrak', 'chiss', 'togruta', 'mirialan', 'cathar',
    'rattataki', 'sith', 'miraluka', 'nautolan', 'rodian', 'trandoshan',
    'jedi', 'sith-hood', 'trooper', 'hunter', 'pilot', 'officer', 'jawa',
    'astromech', 'protocol', 'probe',
];
/** What each avatar shows, for labels and tooltips. */
export const PLAYER_AVATAR_NAMES = {
    'human-a': 'Human',
    'human-b': 'Human',
    twilek: "Twi'lek",
    zabrak: 'Zabrak',
    chiss: 'Chiss',
    togruta: 'Togruta',
    mirialan: 'Mirialan',
    cathar: 'Cathar',
    rattataki: 'Rattataki',
    sith: 'Sith Pureblood',
    miraluka: 'Miraluka',
    nautolan: 'Nautolan',
    rodian: 'Rodian',
    trandoshan: 'Trandoshan',
    jedi: 'Jedi',
    'sith-hood': 'Sith',
    trooper: 'Trooper',
    hunter: 'Bounty Hunter',
    pilot: 'Pilot',
    officer: 'Imperial Officer',
    jawa: 'Jawa',
    astromech: 'Astromech',
    protocol: 'Protocol Droid',
    probe: 'Probe Droid',
};
export function isPlayerAvatarId(value) {
    return typeof value === 'string' && PLAYER_AVATAR_IDS.includes(value);
}
/** Which game types give their players avatars. */
export const GAME_USES_AVATARS = {
    [GameType.HIDE_AND_SEEK]: false,
    [GameType.FP_RACE]: false,
    [GameType.TRAILBLAZERS]: false,
    [GameType.HIDE_AND_SEEK_TEAMS]: true,
};
export function gameUsesAvatars(gameType) {
    return GAME_USES_AVATARS[gameType] ?? false;
}
/**
 * Whether players may change their avatar now: the game uses avatars, has not
 * ended, and no round has started yet. Picks lock for the rest of the game once
 * the first round starts, or begins its countdown to the start (Hide and Seek
 * Teams: the round card already shows everyone's avatar).
 */
export function canPickAvatar(game) {
    if (!gameUsesAvatars(game.gameType))
        return false;
    if (game.status === 'completed' || game.status === 'abandoned')
        return false;
    return !game.rounds.some((round) => round.startedAt || round.status === 'countdown');
}
/** The team a player is on, for keeping avatars unique per team (Hide and Seek Teams' teams). */
function avatarTeamOf(room, playerId) {
    return getHnstTeamOf(room, playerId);
}
/**
 * The players whose avatars `playerId` may not share: everyone in the room who
 * has not left, except players on a different team from theirs. No `playerId`
 * means a player not on a team yet.
 */
function holders(room, playerId) {
    const team = playerId ? avatarTeamOf(room, playerId) : null;
    return room.players.filter((p) => {
        if (p.status === 'left' || p.playerId === playerId || !isPlayerAvatarId(p.avatarId))
            return false;
        const otherTeam = avatarTeamOf(room, p.playerId);
        return !team || !otherTeam || team === otherTeam;
    });
}
/** The player holding `avatarId` whom `playerId` may not share it with, if any. */
export function getAvatarHolder(room, avatarId, playerId) {
    return holders(room, playerId).find((p) => p.avatarId === avatarId);
}
/**
 * An avatar for `playerId` (a player joining the room when not given): a random
 * one no one they would clash with holds, or once all are taken, a random one of
 * the least used.
 */
export function pickFreeAvatar(room, playerId, random = Math.random) {
    const uses = new Map(PLAYER_AVATAR_IDS.map((id) => [id, 0]));
    for (const p of holders(room, playerId)) {
        const id = p.avatarId;
        uses.set(id, (uses.get(id) ?? 0) + 1);
    }
    const fewest = Math.min(...uses.values());
    const candidates = PLAYER_AVATAR_IDS.filter((id) => uses.get(id) === fewest);
    return candidates[Math.min(candidates.length - 1, Math.floor(random() * candidates.length))];
}
/**
 * After the teams change, give a new avatar to anyone who now shares theirs with
 * a teammate. Players already on that team before the change keep theirs; the
 * newcomer gets a random free one. Returns the players whose avatar changed.
 */
export function resolveTeamAvatarClashes(room, previousTeams, random = Math.random) {
    const changed = [];
    for (const team of room.hnstTeams ?? []) {
        const before = new Set(previousTeams?.find((t) => t.key === team.key)?.memberIds ?? []);
        const members = team.memberIds
            .map((id) => room.players.find((p) => p.playerId === id))
            .filter((p) => !!p && p.status !== 'left' && isPlayerAvatarId(p.avatarId))
            .sort((a, b) => Number(before.has(b.playerId)) - Number(before.has(a.playerId)));
        const seen = new Set();
        for (const member of members) {
            if (seen.has(member.avatarId)) {
                member.avatarId = pickFreeAvatar(room, member.playerId, random);
                changed.push(member);
            }
            seen.add(member.avatarId);
        }
    }
    return changed;
}
/** A player's avatar in a game, if they have one. */
export function getPlayerAvatarId(players, playerId) {
    const avatarId = players.find((p) => p.playerId === playerId)?.avatarId;
    return isPlayerAvatarId(avatarId) ? avatarId : undefined;
}
/**
 * The palette hue a player's avatar is drawn in: their team's colour in Hide and
 * Seek Teams once they are on a team, otherwise none (the avatar's own colours).
 */
export function getPlayerAvatarTone(game, playerId) {
    if (game.gameType !== GameType.HIDE_AND_SEEK_TEAMS)
        return undefined;
    const team = getHnstTeamOf(game, playerId);
    return team ? HNST_TEAMS[team].hue : undefined;
}
